/**
 * Copy of the AMS View drilldowns, aggregate tabs and write modals
 * (`components/Ams/view/{drill,actions}`). Spread into `en.amsView`; owns the
 * `drill`, `aggregate`, `asins`, `targets`, `searchTerms`, `negatives`,
 * `altProducts` and `entityModals` sub-keys. VentaHub's English, verbatim,
 * from `pages/ams_view/views/*` and `pages/ams_view/components/*` (cited per
 * block).
 */
export const amsViewDrill = {
  drill: {
    // components/DrilldownToolbar.tsx:55-89 — the drilled ad group above every detail tab.
    titleCampaign: 'Campaign: {{name}}',
    titleStore: 'Store: {{name}}',
    clear: 'Clear selected ad group',
    clearTip: 'Clear the selected ad group',
    // DrilldownToolbar.tsx:112-131
    history: 'Ad group history',
    // views/ASINView.tsx:95-97 (and every write-capable view)
    adGroupPaused: 'Ad group is {{state}} — actions are disabled',
    // ASINView.tsx:71-72 and siblings: what a share or an index reads as.
    ofBid: '{{value}} of bid',
    // components/TotalsNote.tsx:38-81 — why a tab's totals need not equal the Ad groups row.
    totalsNote: {
      asins: {
        short:
          'Totals may differ slightly from the Ad Groups row. Amazon builds each report separately and keeps revising figures for about 14 days after a day closes, so recent days can still differ slightly.',
        long: 'The advertised-product report is a separate request from the ad-group report, though both are now sent together. Amazon also emits one row per merchant SKU with the ad’s figures repeated on each; those are collapsed to one row per ad here, so a SKU sharing an ASIN no longer counts twice.',
      },
      targets: {
        short:
          'Totals may differ slightly from the Ad Groups row. Amazon builds each report separately and keeps revising figures for about 14 days after a day closes, so recent days can still differ slightly.',
        long: 'The targeting report and the ad-group report are separate requests against the same still-settling attribution window, though they are now sent together. Measured per ad-group-day on report-sourced data, the two agree exactly 98.3% of the time; a 30-day total looks worse only because one differing day carries the whole window.',
      },
      searchTerms: {
        short:
          'Totals are lower than the Ad Groups row by design — Amazon reports only search terms that took at least one click, and withholds low-volume queries.',
        long: 'Impressions are affected most: a term can serve thousands of times and never appear here. Clicks and spend usually land within a few units of the ad group’s own figures. Amazon builds each report separately and keeps revising figures for about 14 days after a day closes, so recent days can still differ slightly.',
      },
    },
  },
  // views/AggregateLevelView.tsx — the undrilled detail tabs.
  aggregate: {
    columns: {
      // :427-450
      store: 'Store',
      adGroups: 'Ad Groups',
      adGroupsTip: 'How many ad groups in this store contribute to the row.',
      // :224-241
      asin: 'ASIN',
      skus: 'SKUs',
      // :243-274
      target: 'Target',
      type: 'Type',
      bid: 'Bid',
      bidTip:
        'The range of current bids across the ad groups carrying this target, in this store. Resolved live-master-first with the daily report as fallback.',
      // :275-305
      searchTerm: 'Search Term',
      keywords: 'Matched Keywords',
      keywordsTip:
        'The targets/keywords that surfaced this term. An aggregate — the row is still one search term, however many keywords matched it.',
      match: 'Match',
      // :318-340
      purchasedAsin: 'Purchased ASIN',
      advertisedAsins: 'Advertised ASINs',
      advertisedAsinsTip:
        'Blank for Sponsored Brands and Sponsored Display, which never record an advertised ASIN.',
      // :341-403
      negative: 'Negative',
      status: 'Status',
      statusTip:
        'The most-alive state across the ad groups carrying it — the row answers "is this still blocked anywhere?".',
      lastSeen: 'Last seen',
    },
    // :433 — a store the page has no name for.
    storeFallback: '#{{id}}',
    // :186-194 — one target text, a bid per ad group.
    bidRange: '{{min}} – {{max}}',
    // :198-212
    noEnabled: 'No enabled ad groups carry this in the current period',
    // :547-597 — the disclosures above the table.
    configuration: 'Current configuration — not scoped to the date range',
    mixedCurrency: 'Mixed currencies — totals shown per currency',
    truncated: 'Truncated at {{cap}} rows',
    truncatedTip:
      'Showing the top {{cap}} rows by spend. Narrow the date range, stores or brands to see the rest.',
    skipped_one: '{{count}} non-Amazon store skipped',
    skipped_other: '{{count}} non-Amazon stores skipped',
    // :626-630
    truncatedNote: 'Sorted by spend, so the rows omitted are the smallest spenders.',
    // :619-623
    empty: 'No rows for these filters.',
    search: 'Search',
    // :505-536 — the ad groups behind an expanded row.
    activeIn: 'Active in',
    activeInCount: 'Active in ({{count}})',
    adGroupCount_one: '{{count}} ad group',
    adGroupCount_other: '{{count}} ad groups',
  },
  // views/ASINView.tsx
  asins: {
    columns: { asin: 'ASIN', sku: 'SKU', status: 'Status' },
    empty: 'No advertised products in this ad group.',
    search: 'Search ASINs and SKUs…',
    // :283-300
    add: 'Add Product',
    addTip: 'Advertise another product in this ad group',
    // :238-258
    pause: 'Pause',
    pauseFor: 'Pause {{name}}',
    pauseTip: 'Stop this ad serving',
    enable: 'Enable',
    enableFor: 'Enable {{name}}',
    enableTip: 'Let this ad serve again',
  },
  // views/TargetView.tsx
  targets: {
    columns: { target: 'Target', type: 'Type', status: 'Status', bid: 'Bid' },
    // :202-216 — the -1 sentinel, or a bid that is not a number.
    notAvailable: 'N/A',
    empty: 'No targets in this ad group.',
    search: 'Search targets…',
    // :404-421
    add: 'Add New Target',
    addTip: 'Create a new target in this ad group',
    // :236-279 — the figures of an expanded row.
    bidVsCpc: 'Bid vs CPC',
    bidVsCpcTip:
      'What this target actually paid per click, as a share of its bid. Well under 100% means the bid is not the binding constraint.',
    formulaWouldUse: 'Formula would use',
    formulaWouldUseTip:
      'The conversion rate an ASP-formula bid would multiply by: this target’s measured rate when it has one, otherwise the ad group’s target.',
    formulaValue: '{{value}} ({{source}})',
    source: { actual: 'actual', target: 'target' },
    // :323-379 — the row's actions.
    changeBid: 'Change bid',
    changeBidFor: 'Change bid for {{name}}',
    changeBidTip: "Set this target's bid",
    pause: 'Pause',
    pauseFor: 'Pause {{name}}',
    pauseTip: 'Stop this target serving',
    addNegative: 'Add negative',
    addNegativeFor: 'Add negative for {{name}}',
    addNegativeTip: 'Exclude this target',
    viewSearchTerms: 'View Search Terms',
    viewSearchTermsFor: 'View Search Terms for {{name}}',
    viewSearchTermsTip: 'The shopper queries this target matched',
    viewHistory: 'View History',
    viewHistoryFor: 'View History of {{name}}',
    viewHistoryTip: 'Every recorded change for this target',
    // components/BidInputsTooltip.tsx:84-103 — the Bid cell's hover.
    bidInputs: {
      title: 'ASP bid formula inputs',
      asp: 'ASP',
      targetAcos: 'Target ACOS',
      targetConversion: 'Target conv.',
      actualConversion: 'Actual conv.',
      wouldUseActual: 'Would use (actual)',
      wouldUseTarget: 'Would use (target)',
    },
  },
  // views/SearchTermView.tsx
  searchTerms: {
    columns: { searchTerm: 'Search Term', keyword: 'Keyword' },
    empty: 'No search terms in the selected period.',
    search: 'Search terms and keywords…',
    // :262-275 — which target's terms these are, after the Targets cross-drill.
    targetChip: 'Target: {{target}}',
    // :165-169
    cpcVsAdGroup: 'CPC vs ad group',
    cpcVsAdGroupTip:
      "This term's cost per click relative to the ad group's. Above 1× means it is buying more expensive traffic.",
    // :219-250
    addTarget: 'Add target',
    addTargetFor: 'Add target {{name}}',
    addTargetTip: 'Add this search term as a target',
    alreadyExactTip:
      'Already targeted with this exact text — harvesting adds nothing unless the match type differs',
    addNegative: 'Add negative',
    addNegativeFor: 'Add negative for {{name}}',
    addNegativeTip: 'Stop this search term from matching',
    viewHistory: 'View History',
    viewHistoryFor: 'View History of {{name}}',
    viewHistoryTip: 'Every recorded change for this search term',
  },
  // views/NegativeKeywordsView.tsx
  negatives: {
    columns: {
      keyword: 'Keyword / target',
      type: 'Type',
      matchType: 'Match type',
      status: 'Status',
    },
    // :99 — a negative target excludes an ASIN; a keyword, a phrase.
    type: { target: 'Target', keyword: 'Keyword' },
    typeTip:
      'A negative target excludes an ASIN or category; a negative keyword excludes a search phrase.',
    amazonId: 'Amazon ID',
    amazonIdTip: 'Amazon’s own id for this negative — the handle to quote in a support case.',
    empty: 'No negative keywords on this ad group.',
    search: 'Search negatives…',
    // :219-237
    add: 'Add Negative',
    addTip: 'Exclude another term from this ad group',
    // :166-196
    pause: 'Pause',
    pauseFor: 'Pause {{name}}',
    pauseTip: 'Stop excluding this term',
    enable: 'Enable',
    enableFor: 'Enable {{name}}',
    enableTip: 'Exclude this term again',
  },
  // views/AltProductsView.tsx
  altProducts: {
    // :159-171
    chip: 'Other-ASIN purchases',
    note: "Purchases of a <strong>different product</strong> than the one advertised, attributed to this ad group's ads (Amazon purchased-product report). Only units and sales are reported at this grain.",
    columns: {
      target: 'Target',
      advertisedAsin: 'Advertised ASIN',
      purchasedAsin: 'Purchased ASIN',
    },
    empty: 'No alt-product purchases recorded for this ad group in the selected range.',
    search: 'Search ASINs and targets…',
  },
  // components/actions/* — the write modals only AMS View has.
  entityModals: {
    // SetEntityStateModal.tsx:54-115
    setState: {
      nouns: { ad: 'ad', negative: 'negative keyword' },
      pauseTitle: 'Pause {{noun}}',
      enableTitle: 'Enable {{noun}}',
      pauseQuestion: 'Pause {{label}}?',
      enableQuestion: 'Enable {{label}}?',
      caption:
        'The change is applied on Amazon immediately. This tab will show it once the change is confirmed.',
      pause: 'Pause',
      enable: 'Enable',
      applying: 'Applying…',
      paused: 'Paused {{noun}} "{{label}}".',
      enabled: 'Enabled {{noun}} "{{label}}".',
      failed: 'Could not update the {{noun}}.',
    },
    // AddAdProductModal.tsx:25-125
    addProduct: {
      title: 'Advertise a product',
      asin: 'ASIN',
      sku: 'SKU',
      state: 'State',
      states: { ENABLED: 'Enabled', PAUSED: 'Paused' },
      caption:
        'The ad is created on Amazon immediately and appears here once the next sync reports it.',
      submit: 'Add product',
      submitting: 'Adding…',
      done: 'Added {{asin}} to the ad group.',
      failed: 'Could not add the product.',
    },
    // AddNegativeKeywordModal.tsx:29-118 + negativeMatchTypes.ts:18-31
    addNegative: {
      title: 'Add negative keyword',
      keyword: 'Keyword',
      matchType: 'Match type',
      matchTypes: { exact: 'Negative exact', phrase: 'Negative phrase', broad: 'Negative broad' },
      caption: 'Shoppers searching this term will stop seeing this ad group.',
      submit: 'Add negative',
      submitting: 'Adding…',
      done: 'Added "{{keyword}}" as a negative.',
      failed: 'Could not add the negative keyword.',
    },
    // AddTargetToAdGroupModal.tsx:46-155
    addTarget: {
      title: 'Add target',
      target: 'Target',
      targetPlaceholder: 'keyword text, or an ASIN for a product target',
      matchType: 'Match type',
      matchTypes: { EXACT: 'Exact', PHRASE: 'Phrase', BROAD: 'Broad' },
      bid: 'Bid ({{currency}})',
      caption:
        'The target is created on Amazon immediately. It will appear in this tab once the next sync reports it.',
      submit: 'Add target',
      submitting: 'Adding…',
      done: 'Added "{{target}}" to the ad group.',
      failed: 'Could not add the target.',
    },
  },
} as const
